/**
 * `?thread=<id>` deep link (F-110's other half): open that thread's popover
 * and pan its pin to the middle of the viewport.
 *
 * A notification is a POINTER at a comment, so the link has to survive the
 * fact that the board doc is not there yet when the route mounts. The effect
 * therefore RETRIES on every comment change until the thread appears —
 * following a link to a board you have never opened means waiting on the
 * initial sync, which is exactly when a one-shot attempt would silently do
 * nothing and drop you on a random part of the canvas.
 *
 * The param is stripped (replace, not push) as soon as it is honoured: a
 * reload should not yank a viewport the user has since moved, and Back should
 * not walk through a jump that is not a page.
 */
import { boardToScreen } from "@kola/shared";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useStore } from "zustand";

import type { CanvasStore } from "../../core/canvas.js";
import type { BoardSession } from "../../core/session.js";
import { resolveAnchorPosition } from "./anchoring.js";
import { elementRectLookup } from "./hooks.js";
import type { CommentsUiStore } from "./store.js";

export const THREAD_PARAM = "thread";

export function useThreadDeepLink(
  session: BoardSession,
  canvas: CanvasStore,
  ui: CommentsUiStore,
): void {
  const [params, setParams] = useSearchParams();
  const threadId = params.get(THREAD_PARAM);
  const commentsVersion = useStore(session.store, (s) => s.commentsVersion);
  /** Honour one id once, even though the effect re-runs on every sync tick. */
  const honoured = useRef<string | null>(null);

  useEffect(() => {
    if (threadId === null || honoured.current === threadId) return;
    const thread = session.board.getThread(threadId);
    if (thread === undefined) return; // not synced yet — try again next change

    honoured.current = threadId;
    const position = resolveAnchorPosition(
      thread.anchor,
      elementRectLookup(session),
      ui.getState().gridLayout,
    );
    if (position !== null) {
      // Same centering math as the panel's jump-to-pin (CommentsPanel).
      const { camera, viewport } = canvas.getState();
      const screen = boardToScreen(camera, position);
      canvas.getState().panBy(viewport.width / 2 - screen.x, viewport.height / 2 - screen.y);
    }
    // An orphaned thread (its target was deleted) has no pin to pan to, but the
    // popover still opens — the notification stays honest about what it points
    // at, and the popover says the original location is gone.
    ui.getState().openThread(threadId);

    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(THREAD_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [threadId, commentsVersion, session, canvas, ui, setParams]);
}
